import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import KoobButton from '../KoobButton';

type KoobFormSubmitButtonProps = Omit<
  React.ComponentProps<typeof KoobButton>,
  'type' | 'onClick' | 'loading' | 'asChild'
> & {
  /** Also disable the button until a value differs from the initial ones */
  requireDirty?: boolean;
  /** Set when the button renders outside the <form> element (modal footer) */
  detached?: boolean;
};

// KD-3817 — the one way to submit a Formik form: disabled while the form is
// invalid, and always going through Formik's submission so validation can
// never be skipped by a hand-written onClick.
const KoobFormSubmitButton = ({
  requireDirty = false,
  detached = false,
  disabled,
  variant = 'primary',
  ...props
}: KoobFormSubmitButtonProps) => {
  const {
    isValid,
    dirty,
    isSubmitting,
    initialValues,
    validateForm,
    handleSubmit,
  } = useFormikContext();

  // Formik only validates on change and blur, so an untouched form reports
  // isValid even when its initial values break the schema. Validate them up
  // front (and again after a reinitialization) so the button never depends on
  // the form remembering validateOnMount. A schema test that throws rejects
  // this promise; Formik has already warned about it, so nothing is left to
  // handle here.
  useEffect(() => {
    validateForm().catch(() => undefined);
  }, [validateForm, initialValues]);

  return (
    <KoobButton
      {...props}
      type={detached ? 'button' : 'submit'}
      variant={variant}
      loading={isSubmitting}
      disabled={disabled || !isValid || (requireDirty && !dirty)}
      onClick={detached ? () => handleSubmit() : undefined}
    />
  );
};

export default KoobFormSubmitButton;
